import type { FlagGetter } from "@/FeatureFlag";
import type { Feature } from "@/types/feature";

// One row per feature. Adding a new launch follows this shape: pick the
// unlock flag, the route prefix, and toggle which state-machine branches
// apply. The access hook walks these flags in order — when none of the
// optional branches are enabled the machine collapses to coming-soon → feature.
export type FeatureConfig = {
  // Root path used by the child-route guard when redirecting non-feature
  // states back to the splash root. Must match the route registered in App.tsx.
  rootPath: string;
  // Unlock predicate. Receives the flag getter so any combination of flags can
  // be checked. Omit when the feature is GA for everyone.
  unlockFn?: (flag: FlagGetter) => boolean;
  // Gate the per-user RBAC check (NoPermissions splash). Today the CRO RBAC
  // stub maps to userIsCROAdmin — features outside that scope skip it.
  requiresRBAC: boolean;
  // ai_chat-only consent gate (NeedsAiConsent splash). Other features skip the
  // approval query.
  requiresAiConsent: boolean;
  // Engagement (get-started splash) is opt-in. Features without an associated
  // Get Started CTA leave this false so the access hook resolves directly
  // from `coming-soon` to `feature` once the unlock flag flips.
  requiresEngagement: boolean;
};

// Non-AI CRO features only need the feature flag, not the AI contract gate.
const croUnlocked = (flag: FlagGetter) => flag("freshpaintAiChat");

// ai_chat requires both flags — the AI contract gate applies to the AI product itself.
const freshpaintAiChatEnabled = (flag: FlagGetter) =>
  flag("freshpaintAiChat") && flag("contractAllowsAIUsage");

export const FEATURES: Record<Feature, FeatureConfig> = {
  ai_chat: {
    rootPath: "/ai-chat",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: true,
    requiresEngagement: true,
  },
  web_analytics: {
    rootPath: "/web-analytics",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  funnels: {
    rootPath: "/funnels",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  heatmaps: {
    rootPath: "/heatmaps",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  session_replay: {
    rootPath: "/session-replay",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  surveys: {
    rootPath: "/surveys",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  segments: {
    rootPath: "/audiences/segments",
    unlockFn: (flag) => flag("audiences"),
    requiresRBAC: false,
    // Existing audiences customers must reach the feature without a one-time
    // Get Started splash (CE-1550 AC #5). Flip to true once we have a backfill
    // for engagement on accounts that already had the audiences flag.
    requiresEngagement: false,
    requiresAiConsent: false,
  },
};
